import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Filter,
  Search,
  Bell,
  BellOff,
  Eye,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntlFormatting } from '../utils/intl';
import { iotApi, IoTAlert } from '../lib/iotApi';
import { IS_AWS_MODE } from '../config/frontend';

interface Alert {
  id: string;
  severity: 'info' | 'warn' | 'crit';
  kind: string;
  zone_id?: string;
  zone_name?: string;
  device_name?: string;
  message: string;
  created_at: string;
  acknowledged_by?: string;
  acknowledged_at?: string;
  source?: string;
}

const Alerts: React.FC = () => {
  const { t } = useTranslation('alerts');
  const { formatDateTime } = useIntlFormatting();
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterKind, setFilterKind] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [awsAlerts, setAwsAlerts] = useState<Alert[]>([]);

  const demoAlerts: Alert[] = [
    {
      id: 'alert-001',
      severity: 'crit',
      kind: 'leak',
      zone_id: 'zone-a1',
      zone_name: 'Zone A-1',
      device_name: 'Valve Zone A-1',
      message: 'Potential leak detected: flow rate 7.2 m³/h exceeds baseline by 40%',
      created_at: '2024-01-15T14:30:00Z',
      source: 'Automated Alert System'
    },
    {
      id: 'alert-002',
      severity: 'warn',
      kind: 'low_moisture',
      zone_id: 'zone-b3',
      zone_name: 'Zone B-3',
      device_name: 'Moisture Sensor B-3-01',
      message: 'Low soil moisture detected: 45% below target minimum 60%',
      created_at: '2024-01-15T12:15:00Z',
      acknowledged_by: 'Jean Dupont',
      acknowledged_at: '2024-01-15T13:00:00Z',
      source: 'Moisture Monitoring'
    },
    {
      id: 'alert-003',
      severity: 'warn',
      kind: 'underpressure',
      zone_id: 'zone-c2',
      zone_name: 'Zone C-2',
      device_name: 'Pressure Sensor C-2',
      message: 'Low pressure detected: 1.8 bar below minimum threshold 2.0 bar',
      created_at: '2024-01-15T11:45:00Z',
      source: 'Pressure Monitoring'
    },
    {
      id: 'alert-004',
      severity: 'info',
      kind: 'ph_out_of_range',
      zone_id: 'zone-a2',
      zone_name: 'Zone A-2',
      device_name: 'pH Sensor A-2-01',
      message: 'pH level out of optimal range: 8.2 (recommended: 5.5-7.8)',
      created_at: '2024-01-15T10:20:00Z',
      acknowledged_by: 'Marie Laurent',
      acknowledged_at: '2024-01-15T11:00:00Z',
      source: 'Water Quality Monitoring'
    },
    {
      id: 'alert-005',
      severity: 'crit',
      kind: 'overpressure',
      zone_id: 'zone-b1',
      zone_name: 'Zone B-1',
      device_name: 'Motor Pump Station B',
      message: 'High pressure warning: 6.8 bar exceeds maximum safe threshold 6.0 bar',
      created_at: '2024-01-15T09:30:00Z',
      source: 'Safety Monitoring'
    },
    {
      id: 'alert-006',
      severity: 'warn',
      kind: 'high_ec',
      zone_id: 'zone-c1',
      zone_name: 'Zone C-1',
      device_name: 'EC Sensor C-1-01',
      message: 'Electrical conductivity elevated: 2.8 mS/cm above recommended 2.0 mS/cm',
      created_at: '2024-01-15T08:15:00Z',
      acknowledged_by: 'Pierre Martin',
      acknowledged_at: '2024-01-15T08:45:00Z',
      source: 'Fertigation Monitoring'
    }
  ];

  const loadAwsAlerts = () =>
    iotApi.get('/iot/alerts').then((rows: (IoTAlert & { updated_at?: string })[]) => {
      setAwsAlerts(rows.map<Alert>((alert) => ({
        id: alert.id,
        severity: alert.severity === 'critical' ? 'crit' : alert.severity === 'warning' ? 'warn' : 'info',
        kind: alert.kind,
        device_name: alert.node_name,
        message: alert.message,
        created_at: alert.created_at,
        acknowledged_by: alert.status === 'open' ? undefined : 'Utilisateur AWS',
        acknowledged_at: alert.status === 'open' ? undefined : alert.updated_at,
        source: alert.node_name || 'AWS IoT',
      })));
    });

  useEffect(() => {
    if (IS_AWS_MODE) loadAwsAlerts().catch(console.error);
  }, []);

  const alerts = IS_AWS_MODE ? awsAlerts : demoAlerts;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'crit': return 'var(--color-danger)';
      case 'warn': return 'var(--color-warning)';
      case 'info': return 'var(--color-info)';
      default: return 'var(--text-secondary)';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'crit': return t('severity.critical');
      case 'warn': return t('severity.warn');
      case 'info': return t('severity.info');
      default: return severity;
    }
  };

  const getKindLabel = (kind: string) => {
    switch (kind) {
      case 'leak': return t('kinds.leak');
      case 'overpressure': return t('kinds.overpressure');
      case 'underpressure': return t('kinds.underpressure');
      case 'low_moisture': return t('kinds.lowMoisture');
      case 'high_ec': return t('kinds.highEc');
      case 'ph_out_of_range': return t('kinds.phOutOfRange');
      default: return kind;
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return t('timeAgo.justNow');
    if (diffMins < 60) return t('timeAgo.minutesAgo', { count: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('timeAgo.hoursAgo', { count: diffHours });
    const diffDays = Math.floor(diffHours / 24);
    return t('timeAgo.daysAgo', { count: diffDays });
  };

  const filteredAlerts = alerts.filter(alert => {
    const severityMatch = filterSeverity === 'all' || alert.severity === filterSeverity;
    const statusMatch = filterStatus === 'all' ||
      (filterStatus === 'acknowledged' && alert.acknowledged_by) ||
      (filterStatus === 'unacknowledged' && !alert.acknowledged_by);
    const kindMatch = filterKind === 'all' || alert.kind === filterKind;
    const searchMatch = !searchQuery ||
      alert.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.zone_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.device_name?.toLowerCase().includes(searchQuery.toLowerCase());

    return severityMatch && statusMatch && kindMatch && searchMatch;
  });

  const handleAcknowledge = async (alertId: string) => {
    if (IS_AWS_MODE) {
      await iotApi.post(`/iot/alerts/${alertId}/ack`);
      await loadAwsAlerts();
      setSelectedAlert(null);
      return;
    }
    console.log('Acknowledging alert:', alertId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">{t('title')}</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            {t('subtitle')}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Bell className="w-5 h-5 text-[var(--color-primary)]" />
          <span className="text-sm font-medium text-[var(--text-primary)]">
            {t('unacknowledged', { count: alerts.filter(a => !a.acknowledged_by).length })}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            label: t('stats.totalAlerts'),
            value: alerts.length,
            color: 'var(--color-primary)',
            icon: AlertTriangle
          },
          {
            label: t('stats.critical'),
            value: alerts.filter(a => a.severity === 'crit').length,
            color: 'var(--color-danger)',
            icon: AlertTriangle
          },
          {
            label: t('stats.acknowledged'),
            value: alerts.filter(a => a.acknowledged_by).length,
            color: 'var(--color-success)',
            icon: CheckCircle
          },
          {
            label: t('stats.pending'),
            value: alerts.filter(a => !a.acknowledged_by).length,
            color: 'var(--color-warning)',
            icon: Clock
          }
        ].map((stat, index) => (
          <div key={index} className="card text-center">
            <stat.icon
              className="w-6 h-6 mx-auto mb-2"
              style={{ color: stat.color }}
            />
            <div
              className="text-2xl font-bold mb-1"
              style={{ color: stat.color }}
            >
              {stat.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
              <input
                type="text"
                placeholder={t('filters.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-10"
                data-testid="alert-search-input"
              />
            </div>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-[var(--text-primary)]">{t('filters.severity.label')}</label>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                data-testid="severity-filter"
              >
                <option value="all">{t('filters.severity.all')}</option>
                <option value="crit">{t('filters.severity.critical')}</option>
                <option value="warn">{t('filters.severity.warning')}</option>
                <option value="info">{t('filters.severity.info')}</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-[var(--text-primary)]">{t('filters.status.label')}</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                data-testid="status-filter"
              >
                <option value="all">{t('filters.status.all')}</option>
                <option value="acknowledged">{t('filters.status.acknowledged')}</option>
                <option value="unacknowledged">{t('filters.status.unacknowledged')}</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-[var(--text-primary)]">{t('filters.type.label')}</label>
              <select
                value={filterKind}
                onChange={(e) => setFilterKind(e.target.value)}
                className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
                data-testid="kind-filter"
              >
                <option value="all">{t('filters.type.all')}</option>
                <option value="leak">{t('filters.type.leak')}</option>
                <option value="overpressure">{t('filters.type.overpressure')}</option>
                <option value="underpressure">{t('filters.type.underpressure')}</option>
                <option value="low_moisture">{t('filters.type.lowMoisture')}</option>
                <option value="high_ec">{t('filters.type.highEc')}</option>
                <option value="ph_out_of_range">{t('filters.type.phOutOfRange')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.map((alert) => (
          <div
            key={alert.id}
            className={`card cursor-pointer transition-all hover:shadow-lg ${
              alert.acknowledged_by ? 'opacity-75' : ''
            }`}
            onClick={() => setSelectedAlert(alert)}
            data-testid={`alert-card-${alert.id}`}
          >
            <div className="flex items-start space-x-4">
              {/* Severity Indicator */}
              <div
                className="w-1 h-16 rounded-full"
                style={{ backgroundColor: getSeverityColor(alert.severity) }}
              />

              {/* Alert Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <span
                      className="px-2 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: getSeverityColor(alert.severity) }}
                    >
                      {getSeverityLabel(alert.severity)}
                    </span>
                    <span className="px-2 py-1 text-xs bg-[var(--gray-200)] text-[var(--text-secondary)] rounded-full">
                      {getKindLabel(alert.kind)}
                    </span>
                    {alert.zone_name && (
                      <span className="text-sm font-medium text-[var(--color-primary)]">
                        {alert.zone_name}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-[var(--text-secondary)]">
                      {getTimeAgo(alert.created_at)}
                    </span>
                    {alert.acknowledged_by ? (
                      <CheckCircle className="w-4 h-4 text-[var(--color-success)]" />
                    ) : (
                      <Clock className="w-4 h-4 text-[var(--color-warning)]" />
                    )}
                  </div>
                </div>

                <p className="text-[var(--text-primary)] font-medium mb-2">
                  {alert.message}
                </p>

                <div className="flex items-center justify-between">
                  <div className="text-xs text-[var(--text-secondary)]">
                    {alert.device_name && (
                      <span>{t('source', { source: alert.device_name })} • </span>
                    )}
                    <span>{alert.source}</span>
                  </div>

                  {alert.acknowledged_by ? (
                    <div className="text-xs text-[var(--color-success)]">
                      {t('acknowledged', { user: alert.acknowledged_by })}
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcknowledge(alert.id);
                      }}
                      className="text-xs px-3 py-1 bg-[var(--color-primary)] text-white rounded-full hover:bg-[var(--color-primary-600)] transition-colors"
                      data-testid={`acknowledge-alert-${alert.id}`}
                    >
                      {t('acknowledge')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAlerts.length === 0 && (
        <div className="text-center py-12">
          <BellOff className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">
            {t('noAlertsFound')}
          </h3>
          <p className="text-body text-[var(--text-secondary)]">
            {searchQuery || filterSeverity !== 'all' || filterStatus !== 'all' || filterKind !== 'all'
              ? t('noAlertsMessage')
              : t('allSystemsSmooth')
            }
          </p>
        </div>
      )}

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-[var(--surface)] rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-heading-3 text-[var(--text-primary)]">{t('alertDetails')}</h2>
              <button
                onClick={() => setSelectedAlert(null)}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                data-testid="close-alert-modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span
                  className="px-3 py-1 text-sm font-medium rounded-full text-white"
                  style={{ backgroundColor: getSeverityColor(selectedAlert.severity) }}
                >
                  {getSeverityLabel(selectedAlert.severity)}
                </span>
                <span className="px-3 py-1 text-sm bg-[var(--gray-200)] text-[var(--text-secondary)] rounded-full">
                  {getKindLabel(selectedAlert.kind)}
                </span>
              </div>

              <div>
                <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('message')}</h3>
                <p className="text-[var(--text-primary)]">{selectedAlert.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">{t('zone')}</h4>
                  <p className="text-sm text-[var(--text-secondary)]">
                    {selectedAlert.zone_name || 'N/A'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">{t('device')}</h4>
                  <p className="text-sm text-[var(--text-secondary)]">
                    {selectedAlert.device_name || 'N/A'}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">{t('createdAtLabel')}</h4>
                <p className="text-sm text-[var(--text-secondary)]">
                  {formatDateTime(selectedAlert.created_at)}
                </p>
              </div>

              {selectedAlert.acknowledged_by && (
                <div className="p-4 bg-[var(--color-success)] bg-opacity-10 border border-[var(--color-success)] rounded-lg">
                  <h4 className="text-sm font-medium text-[var(--color-success)] mb-1">
                    {t('acknowledgeAlert')}
                  </h4>
                  <p className="text-sm text-[var(--text-secondary)]">
                    {t('acknowledgedByAt', { user: selectedAlert.acknowledged_by, date: selectedAlert.acknowledged_at ? formatDateTime(selectedAlert.acknowledged_at) : '' })}
                  </p>
                </div>
              )}

              {!selectedAlert.acknowledged_by && (
                <button
                  onClick={() => handleAcknowledge(selectedAlert.id)}
                  className="w-full btn-primary"
                  data-testid="acknowledge-alert-modal"
                >
                  {t('acknowledgeAlert')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
